<template>
  <div>
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <h2 class="text-lg font-medium">{{ i18n('event.title') }}</h2>
    </div>
    <TableFilter
      :disabled="loading"
      :filter-fields="filterFields"
      @filter="doFilter"
      @reset="doResetFilter"
    >
    </TableFilter>
    <!-- BEGIN: HTML Table Data -->
    <div class="intro-y box p-5 mt-5">
      <div
        class="flex flex-col sm:flex-row sm:items-end xl:items-start justify-end"
      >
        <div class="flex mt-5 sm:mt-0">
          <button
            class="btn bg-theme-31 text-white sm:w-auto py-2"
            @click="$router.push('/event/new')"
          >
            <PlusIcon class="w-5 h-5 mr-2 ml-2" /> {{ i18n('common.create') }}
          </button>
        </div>
      </div>
      <div ref="table" class="relative">
        <loading-spinner
          type="half-circle"
          :duration="1000"
          :size="40"
          color="#000"
          :loading="loading"
        />
        <div class="overflow-x-auto">
          <table class="table table-report sm:mt-2">
            <thead>
              <tr>
                <th class="whitespace-nowrap text-center">#</th>
                <th
                  v-for="col in columns"
                  :key="col.name"
                  :id="col.name === 'action' ? 'ignore-1' : ''"
                  class="text-center w-20 whitespace-nowrap"
                >
                  {{ i18n(col.label) }}
                </th>
              </tr>
            </thead>
            <tbody v-if="rows.length">
              <tr v-for="(row, index) in rows" :key="index" class="intro-x">
                <td class="w-40 text-center">
                  <div class="text-gray-600 text-xs whitespace-nowarp mt-0 5">
                    {{ index + 1 }}
                  </div>
                </td>
                <td class="text-center">
                  <div class="font-medium whitespace-nowrap">
                    {{ presenterLocalization(row, 'title') }}
                  </div>
                </td>
                <td class="text-center">
                  <div class="font-medium whitespace-nowrap">
                    {{
                      truncateString(presenterLocalization(row, 'description'))
                    }}
                  </div>
                </td>
                <td class="flex items-center justify-center">
                  <div class="font-medium whitespace-nowrap text-gray-600">
                    <div class="flex-none rounded-md overflow-hidden w-64">
                      <img
                        :src="isRTL ? row?.image?.ar : row?.image?.en"
                        alt=""
                        data-action="zoom"
                      />
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <div class="font-medium whitespace-nowrap">
                    {{ presenterDate(row, 'eventTime') }}
                  </div>
                </td>
                <td class="text-center">
                  <div class="font-medium whitespace-nowrap">
                    {{ presenterLocalization(row, 'location') }}
                  </div>
                </td>
                <td class="table-report__action w-60">
                  <div class="flex justify-center items-center gap-4">
                    <router-link
                      class="text-edit dark:text-theme-30 flex items-center gap-1 whitespace-nowrap"
                      :to="`/event/${row.id}/edit`"
                    >
                      <EditIcon class="w-4 h-4" />
                      {{ i18n('common.edit') }}
                    </router-link>
                    <router-link
                      class="text-view dark:text-theme-30 flex items-center gap-1 whitespace-nowrap"
                      :to="`/event/${row.id}`"
                    >
                      <EyeIcon class="w-4 h-4" />{{ i18n('common.view') }}
                    </router-link>
                    <a
                      href="javascript:;"
                      data-toggle="modal"
                      data-target="#delete-modal-preview"
                      @click="selectedId = row.id"
                      class="text-theme-24 flex items-center gap-1 whitespace-nowrap"
                    >
                      <Trash2Icon class="w-4 h-4" /> {{ i18n('common.delete') }}
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <app-empty-page
            v-if="!rows.length && !loading"
            :label="i18n('empty.event')"
            label-class="text-lg font-medium"
            icon-size="4x"
            class="box w-full py-64"
          ></app-empty-page>
        </div>
        <Pagination
          v-if="rows.length"
          :page="currentPage"
          :pageSize="pagination?.limit"
          :rowsNumber="pagination?.total"
          :showSizeChanger="true"
          :disabled="!rows.length"
          position="center"
          @next-page="doNextPage"
          @prev-change="doPrevPage"
          @size-change="doChangePaginationPageSize"
        ></Pagination>
      </div>
    </div>
    <!-- END: HTML Table Data -->

    <!-- BEGIN: Delete Modal -->
    <DeleteModal item="event" @delete="doDestroy(selectedId)" />
    <!-- END: Delete Modal -->
  </div>
</template>

<script>
import DeleteModal from '@/components/modals/delete-modal.vue'
import { defineComponent } from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { GenericModel } from '@/shared/model/generic-model'

export default defineComponent({
  components: {
    DeleteModal
  },
  data() {
    return {
      filterInput: [],
      selectedId: null,
      columns: [
        {
          name: 'title',
          field: 'title',
          label: 'event.fields.title',
          align: 'center'
        },
        {
          name: 'description',
          field: 'description',
          label: 'event.fields.description',
          align: 'center'
        },
        {
          name: 'image',
          field: 'image',
          label: 'event.fields.image',
          align: 'center'
          // required: true,
        },
        {
          name: 'eventTime',
          field: 'eventTime',
          label: 'event.fields.eventTime',
          align: 'center'
        },
        {
          name: 'location',
          field: 'location',
          label: 'event.fields.location',
          align: 'center'
        },
        {
          name: 'action',
          field: 'action',
          label: 'common.actions',
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      isRTL: 'layout/isRTL',
      language: 'layout/currentLanguageCode',
      rows: 'event/list/rows',
      pagination: 'event/list/pagination',
      listLoading: 'event/list/loading',
      destroyLoading: 'event/destroy/loading'
    }),
    loading() {
      return this.listLoading || this.destroyLoading
    },
    filterFields() {
      return [
        {
          label: this.i18n('iam.filter.title'),
          value: 'title',
          type: 'text',
          localize: true
        },
        {
          label: this.i18n('iam.filter.location'),
          value: 'location',
          type: 'text',
          localize: true
        }
      ]
    }
  },
  created() {
    document.title = this.i18n('routes.app') + ' | ' + this.i18n('routes.event')
  },
  watch: {
    isRTL() {
      document.title =
        this.i18n('routes.app') + ' | ' + this.i18n('routes.event')
    }
  },
  async mounted() {
    await this.doFetchFirstPage()
  },
  methods: {
    ...mapActions({
      doFetch: 'event/list/doFetch',
      doFetchNextPage: 'event/list/doFetchNextPage',
      doFetchPreviousPage: 'event/list/doFetchPreviousPage',
      doChangePaginationCurrentPage: 'event/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize: 'event/list/doChangePaginationPageSize',
      doDestroy: 'event/destroy/doDestroy'
    }),
    async doFilter(filter, orderBy) {
      this.filterInput = []
      if (!filter || !filter.value) return
      if (Array.isArray(filter)) {
        this.filterInput.push(...filter)
      } else {
        this.filterInput.push(filter)
      }
      await this.doFetchFirstPage(orderBy)
    },
    async doResetFilter() {
      this.filterInput = []
      await this.doFetchFirstPage()
    },
    async doFetchFirstPage(orderBy = 'createdAt') {
      await this.doChangePaginationCurrentPage(1)
      await this.doFetch({
        filter: this.filterInput,
        pagination: {
          firstPage: true,
          limit: 10,
          action: 'next',
          orderBy,
          sortType: 'desc'
        }
      })
    },
    async doNextPage() {
      await this.doFetchNextPage()
    },
    async doPrevPage() {
      await this.doFetchPreviousPage()
    },
    i18n(key, args) {
      return this.$t(key, args)
    },
    truncateString(str) {
      return str.length > 100 ? str.slice(0, 100) + '...' : str
    },
    presenter(row, fieldName) {
      return GenericModel.presenter(row, fieldName)
    },
    presenterDate(row, fieldName) {
      return GenericModel.presenterDate(row, fieldName)
    },
    presenterInteger(row, fieldName) {
      return GenericModel.presenterInteger(row, fieldName)
    },
    presenterDecimal(row, fieldName) {
      return GenericModel.presenterDecimal(row, fieldName)
    },
    presenterLocalization(row, fieldName) {
      return GenericModel.presenterLocalization(row, fieldName)
    }
  }
})
</script>

<style lang="scss">
.table-report:not(.table-report--bordered):not(.table-report--tabulator) tr {
  border-radius: 0px !important;
  box-shadow: 15px 0px 15px #0000000b;
}

.table-report:not(.table-report--bordered):not(.table-report--tabulator) td {
  border-radius: 0px !important;
  box-shadow: unset !important;
}

.delete-icon {
  position: absolute;
  width: 80px;
  cursor: pointer;
  margin-top: 200px;
  margin-left: 10px;

  @media only screen and (max-width: 1440px) {
    width: 80px;
    margin-top: 120px;
  }

  @media only screen and (max-width: 1024px) {
    width: 80px;
    margin-top: 80px;
  }

  @media only screen and (max-width: 768px) {
    width: 80px;
    margin-top: 120px;
  }

  @media only screen and (max-width: 425px) {
    width: 80px;
    margin-top: 60px;
  }

  @media only screen and (max-width: 375px) {
    width: 60px;
    margin-top: 60px;
  }

  @media only screen and (max-width: 320px) {
    width: 60px;
    margin-top: 40px;
  }

  @media only screen and (max-width: 300px) {
    width: 60px;
    margin-top: 20px;
  }
}

[dir='rtl'] .delete-icon {
  margin-left: unset !important;
  margin-right: 10px !important;
}
</style>
